import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Stored in memory (survives navigation inside the SPA, resets on full refresh)
const scrollPositions = {};

/**
 * Saves scroll position when leaving the page and restores it
 * once `ready` becomes true (i.e., data has loaded and rendered).
 *
 * @param {boolean} ready - pass `!loading`
 * @param {string}  [key] - optional custom key (defaults to the URL)
 */
const useScrollRestoration = (ready, key) => {
  const location = useLocation();
  const storageKey = key || location.pathname + location.search;
  const restored = useRef(false);

  // Continuously track scroll position
  useEffect(() => {
    const onScroll = () => {
      // only save when page is ready, otherwise the spinner view overwrites it with 0
      if (ready) scrollPositions[storageKey] = window.scrollY;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [storageKey, ready]);

  // Restore once content is rendered
  useLayoutEffect(() => {
    if (!ready || restored.current) return;
    restored.current = true;

    const y = scrollPositions[storageKey];
    if (y) {
      window.scrollTo(0, y);
      // second attempt in case images/layout shift the height
      requestAnimationFrame(() => window.scrollTo(0, y));
    }
  }, [ready, storageKey]);
};

export default useScrollRestoration;